import * as React from 'react';
import { styled } from '@linaria/react';

import useBoundingBox from '@/hooks/use-bounding-box';
import { clamp, normalize } from '@/utils';

import VisuallyHidden from '@/components/VisuallyHidden';
import CoordDisplay from './CoordDisplay';

interface Point {
  x: number;
  y: number;
}

interface Props {
  size: number;
}

function CartesianGraph({ size }: Props) {
  const [point, setPoint] = React.useState<Point>({ x: 110, y: -45 });
  const [isDragging, setIsDragging] = React.useState(false);
  const graphRef = React.useRef<HTMLDivElement>(null);

  const graphBB = useBoundingBox(graphRef, 100);

  const updatePointPosition = React.useCallback(
    (event: PointerEvent | React.PointerEvent) => {
      if (!graphBB) {
        return;
      }

      const min = -size / 2;
      const max = size / 2;

      const x = clamp(
        Math.round(event.clientX - graphBB.left - size / 2),
        min,
        max
      );
      const y = clamp(
        Math.round(event.clientY - graphBB.top - size / 2),
        min,
        max
      );
      setPoint({ x, y });
    },
    [graphBB, size]
  );

  React.useEffect(() => {
    if (!isDragging) {
      return;
    }

    function handlePointerMove(event: PointerEvent) {
      event.preventDefault();
      updatePointPosition(event);
    }

    function handlePointerUp() {
      setIsDragging(false);
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging, updatePointPosition]);

  const handlePointerDown = (event: React.PointerEvent) => {
    event.preventDefault();
    setIsDragging(true);
    updatePointPosition(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setPoint({
        x: point.x,
        y: clamp(point.y + 5, -size / 2, size / 2),
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setPoint({
        x: point.x,
        y: clamp(point.y - 5, -size / 2, size / 2),
      });
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setPoint({
        x: clamp(point.x - 5, -size / 2, size / 2),
        y: point.y,
      });
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setPoint({
        x: clamp(point.x + 5, -size / 2, size / 2),
        y: point.y,
      });
    }
  };

  const showXLabel =
    Math.abs(point.y) > 140 || Math.abs(point.x) > 25;
  const showYLabel =
    Math.abs(point.x) > 140 || Math.abs(point.y) > 25;

  return (
    <Wrapper>
      <Graph
        ref={graphRef}
        onPointerDown={handlePointerDown}
        style={{
          width: size,
          height: size,
        }}
      >
        <GridLines />

        <YAxis />

        <XAxis />

        <XDashedLine
          style={{
            width: Math.abs(point.x),
            top: normalize(point.y, -size / 2, size / 2, 0, size) - 3,
            left:
              point.x > 0
                ? '50%'
                : normalize(point.x, -size / 2, 0, 0, size / 2),
          }}
        />
        <YDashedLine
          style={{
            height: Math.abs(point.y),
            left:
              normalize(point.x, -size / 2, size / 2, 0, size) - 3,
            top:
              point.y > 0
                ? '50%'
                : normalize(point.y, -size / 2, 0, 0, size / 2),
          }}
        />
        <XLabel
          data-flip-sides={point.y < 0}
          style={{
            opacity: showXLabel ? 1 : 0,
            left:
              point.x > 0
                ? normalize(
                    point.x,
                    0,
                    size / 2,
                    size * 0.5,
                    size * 0.75
                  )
                : normalize(
                    point.x,
                    -size / 2,
                    0,
                    size * 0.25,
                    size * 0.5
                  ),
            top: normalize(point.y, -size / 2, size / 2, 0, size) - 2,
          }}
        >
          {point.x}
        </XLabel>

        <YLabel
          data-flip-sides={point.x < 0}
          style={{
            opacity: showYLabel ? 1 : 0,
            top:
              point.y > 0
                ? normalize(
                    point.y,
                    0,
                    size / 2,
                    size * 0.5,
                    size * 0.75
                  )
                : normalize(
                    point.y,
                    -size / 2,
                    0,
                    size * 0.25,
                    size * 0.5
                  ),
            left: normalize(point.x, -size / 2, size / 2, 0, size),
          }}
        >
          {point.y}
        </YLabel>

        <Point
          onKeyDown={handleKeyDown}
          style={{
            transform: `translate(${point.x}px, ${point.y}px)`,
          }}
        >
          <VisuallyHidden>
            X: {point.x}px, Y: {point.y}px
          </VisuallyHidden>
        </Point>
      </Graph>
      <CoordDisplay
        minWidth="6.875rem"
        values={[
          {
            value: point.x + 'px',
            label: 'X (Horizontal)',
          },
          {
            value: point.y + 'px',
            label: 'Y (Vertical)',
          },
        ]}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Graph = styled.div`
  position: relative;
  outline: 2px solid var(--color-gray-200);
  cursor: pointer;
  background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 9px,
      var(--color-gray-200) 9px,
      var(--color-gray-200) 10px
    ),
    repeating-linear-gradient(
      90deg,
      transparent,
      transparent 9px,
      var(--color-gray-200) 9px,
      var(--color-gray-200) 10px
    );
  background-size: calc(100% - 1px) calc(100% - 1px);
  touch-action: none;
`;

const XAxis = styled.div`
  position: absolute;
  top: calc(50% - 1.5px);
  left: -2px;
  right: -2px;
  height: 3px;
  background: var(--color-text);
`;

const YAxis = styled.div`
  position: absolute;
  left: calc(50% - 1.5px);
  top: -2px;
  bottom: -2px;
  width: 3px;
  background: var(--color-text);
`;

const Point = styled.button`
  --size: 16px;

  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--size);
  height: var(--size);
  background: var(--main-color);
  border: 2px solid var(--color-background);
  border-radius: 50%;
  transform-origin: center;
  margin: calc(var(--size) / -2) 0 0 calc(var(--size) / -2);

  &:focus-visible {
    outline: 2px solid var(--main-color);
    outline-offset: 0px;
  }
`;

const DashedLine = styled.div`
  position: absolute;
  opacity: 0.75;
`;

const XDashedLine = styled(DashedLine)`
  height: 0px;
  border-bottom: 3px dashed var(--main-color);
`;

const YDashedLine = styled(DashedLine)`
  width: 0px;
  border-right: 3px dashed var(--main-color);
`;

const Label = styled.div`
  position: absolute;
  transform-origin: center;
  font-size: 0.75rem;
  color: var(--color-text);
  font-weight: var(--font-weight-bold);
  transition: opacity 250ms;
`;

const XLabel = styled(Label)`
  transform: translate(-50%, 4px);

  &[data-flip-sides='true'] {
    transform: translate(-50%, calc(-100% - 4px));
  }
`;

const YLabel = styled(Label)`
  transform: translate(10px, -50%);

  &[data-flip-sides='true'] {
    transform: translate(calc(-100% - 10px), -50%);
  }
`;

const GridLines = styled.div`
  position: absolute;
  inset: 0;
`;

export default CartesianGraph;
